import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useOpenIndividual from '../../../hooks/useOpenIndividual';
import { INDIVIDUAL_ENTRY_POINTS } from '../../../constants/individualOpenModes';
import {
  FaArrowLeft,
  FaSortAmountDown,
  FaSortAmountUp,
} from 'react-icons/fa';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, MainSection, BackButton } from '../styles';
import {
  FiltersBar,
  HeaderStats,
  HeaderStat,
  EmployeeRow,
  EmployeeInfo,
  LagBadge,
  DateInfo,
  LoadMoreButton,
  SortButton,
} from './styles';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { useBenchBusinessUnitFacets } from '../../../hooks/useBenchBusinessUnitFacets';
import { getBusinessUnitFacetCount } from '../../../utils/benchBusinessUnitFacets';
import {
  BENCH_REPORT_KEYS,
  BENCH_BUSINESS_UNIT_COUNT_TOOLTIP,
  BENCH_BUSINESS_UNIT_EMPTY_LABEL,
} from '../../../constants/benchReportFacets';
import ResultsCount from '../../common/ResultsCount';
import EmployeeSearch from '../../common/EmployeeSearch';
import { EmployeeSearchProvider } from '../../common/EmployeeSearch/contexts/EmployeeSearchProvider';
import ClusterSelector from '../../common/ClusterSelector';
import BusinessUnitSelector from '../../common/BusinessUnitSelector';
import { benchApi } from '../../../services/api/benchApi';
import { BENCH_TYPES, getBenchTypeLabel } from '../../../constants/benchTypes';
import { formatDateOnly } from '../../../utils/dateOnly';

const formatDate = (dateStr) => {
  if (!dateStr) { return '—'; }
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const LAG_RANGES = [
  { value: '', label: 'All', color: null },
  { value: 'pre_registered', label: 'Pre-registered', color: '#2563EB' },
  { value: 'same_day', label: 'Same-day (0d)', color: '#16A34A' },
  { value: '1_3', label: '1–3 days', color: '#D97706' },
  { value: '4_7', label: '4–7 days', color: '#EA580C' },
  { value: '8_14', label: '8–14 days', color: '#DC2626' },
  { value: '15_plus', label: '15+ days', color: '#DC2626' },
];

const BenchEntryLagReport = () => {
  const navigate = useNavigate();
  const openIndividual = useOpenIndividual();
  const location = useLocation();
  const mainSectionRef = useRef(null);
  const scrollRestoredRef = useRef(false);

  // Restore state when returning from employee profile
  const restored = location.state?.benchEntryLagState;

  // Filter state
  const [clusterId, setClusterId] = useState(restored?.clusterId || '');
  const [businessUnitId, setBusinessUnitId] = useState(restored?.businessUnitId || '');
  const [benchType, setBenchType] = useState(restored?.benchType || '');
  const [lagRange, setLagRange] = useState(restored?.lagRange || '');
  const [selectedEmployee, setSelectedEmployee] = useState(restored?.selectedEmployee || null);
  const [orderBy, setOrderBy] = useState(restored?.orderBy || 'lagDays');
  const [orderDirection, setOrderDirection] = useState(restored?.orderDirection || 'desc');

  // ADR-108: Business Unit options = BUs with individuals on bench in the selected
  // Cluster (other filters do not narrow them). Out-of-scope selection is cleared.
  const {
    businessUnitsData,
    countMode: businessUnitCountMode,
    isLoading: isLoadingBusinessUnits,
    failed: businessUnitFacetsFailed,
  } = useBenchBusinessUnitFacets({
    report: BENCH_REPORT_KEYS.BENCH_ENTRY_LAG,
    clusterId,
    businessUnitId,
    onSelectionOutOfScope: () => setBusinessUnitId(''),
  });

  // Data state
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [employees, setEmployees] = useState([]);

  const fetchReport = useCallback(async (cursor = undefined, append = false) => {
    try {
      if (!append) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const params = {
        limit: 50,
        orderBy,
        orderDirection,
        cursor,
      };

      if (clusterId) {
        params.clusterId = clusterId;
      }
      if (businessUnitId) {
        params.businessUnitId = businessUnitId;
        params.buScope = 'hierarchy';
      }
      if (benchType) {
        params.benchType = benchType;
      }
      if (lagRange) {
        params.lagRange = lagRange;
      }
      if (selectedEmployee) {
        params.employeeId = selectedEmployee.id;
      }

      const data = await benchApi.getBenchEntryLagReport(params);

      if (append) {
        setEmployees((prev) => [...prev, ...data.employees]);
      } else {
        setEmployees(data.employees);
      }

      setReport(data);
    } catch (error) {
      console.error('Error fetching bench entry lag report:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [clusterId, businessUnitId, benchType, lagRange, selectedEmployee, orderBy, orderDirection]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleLoadMore = () => {
    if (report?.pageInfo?.hasMore && report.pageInfo.cursor) {
      fetchReport(report.pageInfo.cursor, true);
    }
  };

  const toggleSortField = (field) => {
    if (orderBy === field) {
      setOrderDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setOrderBy(field);
      setOrderDirection('desc');
    }
  };

  // Restore scroll position after data loads on return from employee profile
  useEffect(() => {
    if (!isLoading && restored?.scrollTop && !scrollRestoredRef.current && mainSectionRef.current) {
      mainSectionRef.current.scrollTop = restored.scrollTop;
      scrollRestoredRef.current = true;
    }
  }, [isLoading, restored]);

  const handleNavigateToEmployee = useCallback((emp) => {
    const benchEntryLagState = {
      clusterId,
      businessUnitId,
      benchType,
      lagRange,
      selectedEmployee,
      orderBy,
      orderDirection,
      scrollTop: mainSectionRef.current?.scrollTop || 0,
    };
    // ADR-109 — auto open; returnLabel was missing (D3: the button read "Back to Individuals")
    openIndividual(emp.employee.id, {
      entryPoint: INDIVIDUAL_ENTRY_POINTS.BENCH_ENTRY_LAG,
      returnTo: '/reports/bench-entry-lag',
      returnLabel: 'Bench Data Entry Lag',
      state: { benchEntryLagState },
    });
  }, [clusterId, businessUnitId, benchType, lagRange, selectedEmployee, orderBy, orderDirection, openIndividual]);

  const summary = report?.summary || {
    totalActive: 0,
    averageLagDays: 0,
    maxLagDays: 0,
    sameDayCount: 0,
    sameDayPercent: 0,
    lateCount: 0,
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/reports')}>
            <FaArrowLeft /> Back to Reports
          </BackButton>
          <Title>Bench Data Entry Lag</Title>
        </HeaderLeft>
        <HeaderStats>
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <>
              <HeaderStat>
                <div className="top">
                  <span className="label">Total</span>
                  <span className="value">{summary.totalActive}</span>
                </div>
              </HeaderStat>
              <HeaderStat>
                <div className="top">
                  <span className="label">Avg. Lag</span>
                  <span className="value">{summary.averageLagDays}d</span>
                </div>
              </HeaderStat>
              <HeaderStat>
                <div className="top">
                  <span className="label">Max Lag</span>
                  <span className="value">{summary.maxLagDays}d</span>
                </div>
              </HeaderStat>
              <HeaderStat>
                <div className="top">
                  <span className="label">Same-Day</span>
                  <span className="value">{summary.sameDayCount}</span>
                </div>
                <span className="sub">{summary.sameDayPercent}% of total</span>
              </HeaderStat>
              <HeaderStat>
                <div className="top">
                  <span className="label">Late (&gt;3d)</span>
                  <span className="value">{summary.lateCount}</span>
                </div>
              </HeaderStat>
            </>
          )}
        </HeaderStats>
      </Header>

      <Content>
        <MainSection ref={mainSectionRef}>
          {/* Filters */}
          <FiltersBar>
            <ClusterSelector
              value={clusterId}
              onChange={setClusterId}
              label="Cluster"
              showStructureType // ADR-119
              size="small"
              fullWidth={false}
              sx={{ minWidth: 180 }}
            />

            {businessUnitFacetsFailed ? (
              // Degrade, don't fail (ADR-108 §5): plain hierarchy, scoped by Cluster
              <BusinessUnitSelector
                includeInactive // ADR-120: reports keep ended units, marked "Ended <date>"
                value={businessUnitId}
                onChange={setBusinessUnitId}
                label="Business Unit"
                showStructureType // ADR-119
                size="small"
                fullWidth={false}
                sx={{ minWidth: 180 }}
                clusterId={clusterId || null}
              />
            ) : (
              <BusinessUnitSelector
                includeInactive // ADR-120: reports keep ended units, marked "Ended <date>"
                value={businessUnitId}
                onChange={setBusinessUnitId}
                label="Business Unit"
                showStructureType // ADR-119
                size="small"
                fullWidth={false}
                sx={{ minWidth: 180 }}
                businessUnitsData={businessUnitsData}
                loading={isLoadingBusinessUnits}
                getItemCount={(unit) => getBusinessUnitFacetCount(unit, businessUnitCountMode)}
                itemCountTooltip={BENCH_BUSINESS_UNIT_COUNT_TOOLTIP}
                hideClusterChip={Boolean(clusterId)}
                emptyStateLabel={BENCH_BUSINESS_UNIT_EMPTY_LABEL}
              />
            )}

            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Bench Type</InputLabel>
              <Select
                value={benchType}
                label="Bench Type"
                onChange={(e) => setBenchType(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {Object.values(BENCH_TYPES).map((type) => (
                  <MenuItem key={type} value={type}>
                    {getBenchTypeLabel(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Lag Range</InputLabel>
              <Select
                value={lagRange}
                label="Lag Range"
                onChange={(e) => setLagRange(e.target.value)}
                renderValue={(value) => {
                  const range = LAG_RANGES.find((r) => r.value === value);
                  if (!range) { return ''; }
                  return (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {range.color && (
                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: range.color, flexShrink: 0 }} />
                      )}
                      {range.label}
                    </span>
                  );
                }}
              >
                {LAG_RANGES.map((range) => (
                  <MenuItem key={range.value} value={range.value} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {range.color && (
                      <span style={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: range.color,
                        flexShrink: 0,
                      }} />
                    )}
                    {range.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <EmployeeSearchProvider instanceId="bench-entry-lag-search">
              <EmployeeSearch
                value={selectedEmployee}
                onChange={setSelectedEmployee}
                label="Individual"
                placeholder="Search individuals..."
                width={260}
                height={40}
                hideLabel
              />
            </EmployeeSearchProvider>
          </FiltersBar>

          {/* Sort controls */}
          {!isLoading && employees.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <ResultsCount count={summary.totalActive} singular="individual" plural="individuals" />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <SortButton
                  onClick={() => toggleSortField('lagDays')}
                  style={orderBy === 'lagDays' ? { fontWeight: 700 } : {}}
                >
                  {orderBy === 'lagDays' && (orderDirection === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />)}
                  Lag days
                </SortButton>
                <SortButton
                  onClick={() => toggleSortField('startDate')}
                  style={orderBy === 'startDate' ? { fontWeight: 700 } : {}}
                >
                  {orderBy === 'startDate' && (orderDirection === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />)}
                  Start date
                </SortButton>
              </Box>
            </div>
          )}

          {/* Employee list */}
          {isLoading ? (
            <LoadingSpinner />
          ) : employees.length === 0 ? (
            <EmptyState
              title="No Results Found"
              description="No bench individuals match the current filters. Try adjusting your search criteria."
            />
          ) : (
            <>
              {employees.map((emp) => (
                <EmployeeRow key={emp.employeeProjectId}>
                  <EmployeeAvatar employee={emp.employee} size={40} />

                  <EmployeeInfo>
                    <div
                      className="name name-link"
                      onClick={() => handleNavigateToEmployee(emp)}
                    >
                      {emp.employee.fullName}
                    </div>
                    <div className="detail">
                      {emp.employee.position?.label || 'No position'}
                      {emp.employee.grade?.label && ` • ${emp.employee.grade.label}`}
                    </div>
                    <div className="detail">
                      {[
                        emp.cluster?.name,
                        emp.businessUnit?.name,
                        emp.directManager ? `Representative: ${emp.directManager.fullName}` : null,
                      ].filter(Boolean).join(' • ')}
                    </div>
                  </EmployeeInfo>

                  <DateInfo>
                    <span className="date-label">Bench Start</span>
                    <span className="date-value">{formatDateOnly(emp.benchStartDate, 'DD/MM/YYYY', '—')}</span>
                  </DateInfo>

                  <DateInfo>
                    <span className="date-label">Registered</span>
                    <span className="date-value">{formatDate(emp.registeredAt)}</span>
                  </DateInfo>

                  <LagBadge $lagDays={emp.lagDays}>
                    {emp.lagDays === 0 ? '0d' : emp.lagDays > 0 ? `+${emp.lagDays}d` : `${emp.lagDays}d`}
                  </LagBadge>
                </EmployeeRow>
              ))}

              {report?.pageInfo?.hasMore && (
                <LoadMoreButton onClick={handleLoadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? (
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                  ) : null}
                  Load More
                </LoadMoreButton>
              )}
            </>
          )}
        </MainSection>
      </Content>
    </Container>
  );
};

export default BenchEntryLagReport;
